import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Info,
  Pencil,
} from "lucide-react";
import toast from "react-hot-toast";
import { apiService } from "../services/api";
import { DataActLinkedAnswerDetails, DataActLinkedCandidate } from "../types";
import {
  DataActLinkedMode,
  linkedFlowQuestionIds,
  linkedFlowStateQuestionId,
} from "../utils/dataActLinkedFlow";

type Mode = DataActLinkedMode;

interface ChildQuestionIds {
  trigger: string; // Q2.5 or Q2.11 (not written by this component, only read + details used)
  name: string; // Q2.6 or Q2.12
  owner: string; // Q2.7 or Q2.14
  completed: string; // Q2.8 or Q2.15
}

const MODE_CONFIG: Record<
  Mode,
  {
    label: string;
    peerTypeLabel: string; // text shown in filter hint
    classificationKey: "relatedService" | "connectedProduct";
    ids: ChildQuestionIds;
  }
> = {
  "related-service": {
    label: "Related Service",
    peerTypeLabel: "software",
    classificationKey: "relatedService",
    ids: {
      trigger: "Q2.5",
      ...linkedFlowQuestionIds("related-service"),
    },
  },
  "connected-product": {
    label: "Connected Product",
    peerTypeLabel: "hardware",
    classificationKey: "connectedProduct",
    ids: {
      trigger: "Q2.11",
      ...linkedFlowQuestionIds("connected-product"),
    },
  },
};

interface DataActLinkedProductFlowProps {
  productId: string;
  mode: Mode;
  /** Current plain answers (by question id). */
  answers: Record<string, { answer: string; details: string }>;
  /**
   * Bulk writer. Writes a set of (questionId, field) -> value updates to
   * parent state in one render. Used so selecting a peer updates Q2.6/2.7/2.8
   * and the picker state atomically.
   */
  onChange: (
    questionId: string,
    field: "answer" | "details",
    value: string,
  ) => void;
  /** Flips the trigger (Q2.5 / Q2.11) answer back to "no". */
  onResetTriggerToNo: () => void;
  /**
   * Optional hook called before the user is navigated away to edit the
   * product's relationships. Used by the parent to persist the assessment
   * draft so the user lands back on the same section after saving.
   */
  onSaveBeforeNavigate?: () => Promise<unknown> | void;
  disabled?: boolean;
}

/**
 * Read picker state from the synthetic state answer entry.
 * `answer` holds the mode ("linked" | "manual"); `details` holds a JSON
 * array of selected peer product ids.
 */
function parsePickerState(entry: {
  answer: string;
  details: string;
}): DataActLinkedAnswerDetails {
  const mode: "linked" | "manual" =
    entry.answer === "manual" ? "manual" : "linked";
  let peerProductIds: string[] = [];
  if (entry.details) {
    try {
      const parsed = JSON.parse(entry.details);
      if (Array.isArray(parsed)) {
        peerProductIds = parsed.filter(
          (v: unknown): v is string => typeof v === "string",
        );
      }
    } catch {
      // ignore — empty selection
    }
  }
  return { mode, peerProductIds };
}

function serialisePeerIds(ids: string[]): string {
  return ids.length > 0 ? JSON.stringify(ids) : "";
}

export default function DataActLinkedProductFlow({
  productId,
  mode,
  answers,
  onChange,
  onResetTriggerToNo,
  onSaveBeforeNavigate,
  disabled = false,
}: DataActLinkedProductFlowProps) {
  const navigate = useNavigate();
  const config = MODE_CONFIG[mode];
  const { ids } = config;
  const stateQuestionId = linkedFlowStateQuestionId(mode);

  const { data: candidatesRes, isLoading } = useQuery(
    ["dataActLinkedCandidates", productId, mode],
    () => apiService.getDataActLinkedCandidates(productId, mode),
    { enabled: !!productId, retry: false },
  );
  const candidates = useMemo<DataActLinkedCandidate[]>(
    () => candidatesRes?.data?.data ?? [],
    [candidatesRes],
  );

  const stateEntry = answers[stateQuestionId];
  const state = useMemo(
    () => parsePickerState(stateEntry ?? { answer: "", details: "" }),
    [stateEntry],
  );
  const isManual = state.mode === "manual";
  const selectedIds = useMemo(
    () => state.peerProductIds ?? [],
    [state.peerProductIds],
  );

  const selectedCandidates = useMemo(
    () => candidates.filter((c) => selectedIds.includes(c.id)),
    [candidates, selectedIds],
  );

  const writeState = (nextMode: "linked" | "manual", nextPeerIds: string[]) => {
    onChange(stateQuestionId, "answer", nextMode);
    onChange(stateQuestionId, "details", serialisePeerIds(nextPeerIds));
  };

  // Apply selection → auto-fill Q2.6/2.7/2.8 (or mirrors) and persist state.
  const applySelection = (nextIds: string[]) => {
    const selected = candidates.filter((c) => nextIds.includes(c.id));
    writeState("linked", nextIds);
    onChange(ids.name, "answer", selected.map((c) => c.name).join(", "));
    onChange(
      ids.owner,
      "answer",
      selected.map((c) => c.ownerName || c.ownerEmail).join(", "),
    );
    const allGreen =
      selected.length > 0 &&
      selected.every(
        (c) =>
          c.latestAssessmentStatus === "submitted" &&
          c[config.classificationKey],
      );
    onChange(ids.completed, "answer", allGreen ? "yes" : "no");
  };

  const togglePeer = (peerId: string) => {
    if (disabled) return;
    const next = selectedIds.includes(peerId)
      ? selectedIds.filter((id) => id !== peerId)
      : [...selectedIds, peerId];
    applySelection(next);
  };

  const switchToManual = () => {
    if (disabled) return;
    // Clear the picker-driven auto-fills so the user starts from a blank slate
    // in manual mode. They can switch back without data loss in the picker —
    // selections are recomputed from candidate data.
    writeState("manual", []);
    onChange(ids.name, "answer", "");
    onChange(ids.owner, "answer", "");
    onChange(ids.completed, "answer", "");
  };

  const switchToLinked = () => {
    if (disabled) return;
    writeState("linked", []);
    onChange(ids.name, "answer", "");
    onChange(ids.owner, "answer", "");
    onChange(ids.completed, "answer", "");
  };

  const goEditRelationships = async () => {
    try {
      await onSaveBeforeNavigate?.();
    } catch {
      // Saving failed — surface via the parent's mutation toast and abort
      // navigation so the user doesn't lose their place.
      return;
    }
    navigate(
      `/products/${productId}?edit=true&focus=relationships&resumeAfter=data-act`,
    );
  };

  if (isLoading) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 py-4">
        Loading linked products…
      </div>
    );
  }

  // ── Manual fallback path ──
  if (isManual) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-start gap-2 mb-3">
          <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-gray-700 dark:text-gray-300">
            You are entering the {config.label} details manually. Where
            possible, prefer linking to an existing product so its Data Act
            assessment can be used automatically.
          </p>
        </div>
        <ManualFields
          nameId={ids.name}
          ownerId={ids.owner}
          completedId={ids.completed}
          answers={answers}
          onChange={onChange}
          disabled={disabled}
        />
        {!disabled && (
          <button
            type="button"
            onClick={switchToLinked}
            className="mt-3 text-xs text-primary-600 dark:text-primary-400 hover:underline"
          >
            Use picker instead
          </button>
        )}
      </div>
    );
  }

  // ── Gate: user answered YES but no eligible candidates exist ──
  if (candidates.length === 0) {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 p-5">
        <div className="flex items-start gap-2 mb-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              No eligible {config.peerTypeLabel} products linked
            </h4>
            <p className="mt-1 text-sm text-amber-800 dark:text-amber-300 leading-relaxed">
              This product has no <strong>interfaces-with</strong> relationship
              to a {config.peerTypeLabel} product. To proceed, pick one of the
              options below.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {!disabled && (
            <button
              type="button"
              onClick={onResetTriggerToNo}
              className="btn btn-primary text-sm"
            >
              Change answer to No
            </button>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={goEditRelationships}
              className="btn btn-outline text-sm"
            >
              <Pencil className="h-4 w-4 mr-1" />
              Add a link on the product
            </button>
          )}
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={switchToManual}
            className="mt-3 text-xs text-gray-600 dark:text-gray-400 hover:underline"
          >
            Or enter the {config.label.toLowerCase()} details manually (not
            recommended)
          </button>
        )}
      </div>
    );
  }

  // ── Picker + classification cards ──
  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
          Select the linked {config.peerTypeLabel} product(s) that represent the{" "}
          <b>potential</b> {config.label.toLowerCase()}. Only peers linked to
          this product via <em>interfaces-with</em> and classified as{" "}
          <em>{config.peerTypeLabel}</em> are shown.
        </p>
        <div className="space-y-2">
          {candidates.map((c) => {
            const checked = selectedIds.includes(c.id);
            return (
              <label
                key={c.id}
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  checked
                    ? "border-primary-300 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-700"
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={checked}
                  onChange={() => togglePeer(c.id)}
                  disabled={disabled}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {c.name}
                    </span>
                    <AssessmentBadge candidate={c} mode={mode} />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                    {c.ownerName || "—"}
                    {c.ownerEmail ? ` · ${c.ownerEmail}` : ""}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {selectedCandidates.length > 0 && (
        <div className="space-y-3">
          {selectedCandidates.map((c) => (
            <ClassificationCard key={c.id} candidate={c} mode={mode} />
          ))}
        </div>
      )}

      {!disabled && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500 dark:text-gray-400">
          <button
            type="button"
            onClick={goEditRelationships}
            className="inline-flex items-center gap-1 hover:underline"
          >
            <Pencil className="h-3 w-3" />
            Edit links on the product
          </button>
          <button
            type="button"
            onClick={switchToManual}
            className="hover:underline"
          >
            Switch to manual entry (not recommended)
          </button>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──

function AssessmentBadge({
  candidate,
  mode,
}: {
  candidate: DataActLinkedCandidate;
  mode: Mode;
}) {
  const key = MODE_CONFIG[mode].classificationKey;
  if (candidate.latestAssessmentStatus === "submitted" && candidate[key]) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
        <CheckCircle2 className="h-3 w-3" /> Assessed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
      <AlertTriangle className="h-3 w-3" /> Pending
    </span>
  );
}

function ClassificationCard({
  candidate,
  mode,
}: {
  candidate: DataActLinkedCandidate;
  mode: Mode;
}) {
  const config = MODE_CONFIG[mode];
  const isGreen =
    candidate.latestAssessmentStatus === "submitted" &&
    candidate[config.classificationKey];

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Failed to copy ${label.toLowerCase()}`);
    }
  };

  const assessmentLink = `${window.location.origin}/products/${candidate.id}?tab=data-act`;

  if (isGreen) {
    return (
      <div className="rounded-md border border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-700 p-3">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="h-5 w-5 text-green-700 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h5 className="text-sm font-semibold text-green-900 dark:text-green-200">
              {candidate.name} — Classified as {config.label}
            </h5>
            <p className="mt-0.5 text-xs text-green-800 dark:text-green-300">
              Owner: {candidate.ownerName || "—"}
              {candidate.ownerEmail ? ` · ${candidate.ownerEmail}` : ""}
            </p>
            <p className="mt-0.5 text-xs text-green-800 dark:text-green-300">
              Data Act assessment submitted.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-yellow-700 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h5 className="text-sm font-semibold text-yellow-900 dark:text-yellow-200">
            {candidate.name} — Assessment pending
          </h5>
          <p className="mt-0.5 text-xs text-yellow-800 dark:text-yellow-300">
            Owner: {candidate.ownerName || "—"}
            {candidate.ownerEmail ? ` · ${candidate.ownerEmail}` : ""}
          </p>
          <p className="mt-1 text-xs text-yellow-800 dark:text-yellow-300 leading-relaxed">
            {candidate.latestAssessmentStatus === "submitted"
              ? `The product's latest submitted Data Act assessment does not classify it as a ${config.label}.`
              : `This product does not have a submitted Data Act assessment yet.`}{" "}
            Please contact the owner and ask them to complete the assessment.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {candidate.ownerEmail && (
              <button
                type="button"
                onClick={() => copy(candidate.ownerEmail, "Owner email")}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-yellow-300 dark:border-yellow-700 text-yellow-900 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900/40"
              >
                <Copy className="h-3 w-3" /> Copy owner email
              </button>
            )}
            <button
              type="button"
              onClick={() => copy(assessmentLink, "Assessment link")}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-yellow-300 dark:border-yellow-700 text-yellow-900 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900/40"
            >
              <ExternalLink className="h-3 w-3" /> Copy assessment link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManualFields({
  nameId,
  ownerId,
  completedId,
  answers,
  onChange,
  disabled,
}: {
  nameId: string;
  ownerId: string;
  completedId: string;
  answers: Record<string, { answer: string; details: string }>;
  onChange: (
    questionId: string,
    field: "answer" | "details",
    value: string,
  ) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name
        </label>
        <input
          type="text"
          className="input w-full"
          value={answers[nameId]?.answer ?? ""}
          onChange={(e) => onChange(nameId, "answer", e.target.value)}
          disabled={disabled}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Owner / point of contact
        </label>
        <input
          type="text"
          className="input w-full"
          value={answers[ownerId]?.answer ?? ""}
          onChange={(e) => onChange(ownerId, "answer", e.target.value)}
          disabled={disabled}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Has the owner completed the corresponding Data Act assessment?
        </label>
        <div className="grid grid-cols-2 gap-2">
          {["yes", "no"].map((v) => {
            const selected = answers[completedId]?.answer === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => onChange(completedId, "answer", v)}
                disabled={disabled}
                className={`py-2 px-2 text-sm font-medium rounded-lg border transition-colors ${
                  selected
                    ? "bg-primary-50 border-primary-300 text-primary-800 dark:bg-primary-900/20 dark:border-primary-700 dark:text-primary-200"
                    : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                } disabled:opacity-50`}
              >
                {v === "yes" ? "Yes" : "No"}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
